import { useState } from 'react';

import { Avatar } from '../../shared/ui/avatar';
import { Button } from '../../shared/ui/button';
import { ConfirmDelete } from '../../shared/ui/confirm-delete';
import { CopyField } from '../../shared/ui/copy-field';
import { Hint } from '../../shared/ui/hint';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import { Note } from '../../shared/ui/note';
import { SectionPanel } from '../../shared/ui/section-panel';
import { Select } from '../../shared/ui/select';
import { Table, type IColumn } from '../../shared/ui/table';
import { Tooltip } from '../../shared/ui/tooltip';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { ApiError } from '../../shared/api/client';
import { clockTime, shortDate } from '../../shared/lib/format';
import {
  ownerCount,
  removalBlock,
  roleBlock,
  roleOptionsFor,
  seatLine,
  type IActor,
  type TMember,
} from '../../entities/organization';
import type { TMembers } from './use-members';

interface IPeopleProps {
  members: TMembers;
  actor: IActor | null;
  timeZone?: string;
}

const nameOf = (member: TMember) => member.userName ?? member.userEmail ?? 'Unnamed account';

interface IColumnsArgs {
  members: TMembers;
  actor: IActor | null;
  owners: number;
  onResetLink: (member: TMember) => void;
  onRemove: (member: TMember) => void;
}

const columns = ({
  members,
  actor,
  owners,
  onResetLink,
  onRemove,
}: IColumnsArgs): IColumn<TMember>[] => [
  {
    key: 'person',
    header: 'Person',
    width: 'grow',
    ui: true,
    render: (member) => (
      <span className="flex min-w-0 items-center gap-3">
        <Avatar name={nameOf(member)} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-primary">
            {nameOf(member)}
            {actor?.userId === member.userId && (
              <span className="ml-2 font-mono text-micro text-faint">you</span>
            )}
          </span>
          <span className="truncate font-mono text-meta text-muted">
            {member.userEmail ?? 'no email on the account'}
          </span>
        </span>
      </span>
    ),
  },
  {
    key: 'role',
    header: 'Role',
    width: 'w-col-role',
    ui: true,
    render: (member) => {
      const blocked = roleBlock(actor, member);
      const control = (
        <span className="flex w-full">
          <Select
            className="h-control-sm text-data"
            aria-label={`Role of ${nameOf(member)}`}
            value={member.role}
            disabled={blocked !== null || members.role.isPending}
            options={roleOptionsFor(actor)}
            onChange={(next) => members.role.mutate({ userId: member.userId, next })}
          />
        </span>
      );
      return blocked === null ? control : <Tooltip content={blocked}>{control}</Tooltip>;
    },
  },
  {
    key: 'actions',
    header: '',
    width: 'w-col-row-actions',
    ui: true,
    render: (member) => {
      if (actor?.userId === member.userId) return null;
      const blocked = removalBlock(actor, member, owners);
      const remove = (
        <span className="flex">
          <Button
            variant="ghost"
            size="sm"
            disabled={blocked !== null || members.remove.isPending}
            onClick={() => onRemove(member)}
          >
            Remove
          </Button>
        </span>
      );
      return (
        <span className="flex items-center justify-end gap-1">
          {actor?.role === 'owner' && (
            <Button variant="ghost" size="sm" onClick={() => onResetLink(member)}>
              Reset link
            </Button>
          )}
          {blocked === null ? remove : <Tooltip content={blocked}>{remove}</Tooltip>}
        </span>
      );
    },
  },
];

export const MembersPanel = ({ members, actor, timeZone }: IPeopleProps) => {
  const [minted, setMinted] = useState<TMember | null>(null);
  const [removing, setRemoving] = useState<TMember | null>(null);
  const rows = members.members.data ?? [];
  const owners = ownerCount(rows);
  const pending = members.invitations.data?.length ?? 0;

  const openResetLink = (member: TMember) => {
    setMinted(member);
    members.resetLink.mutate(member.userId);
  };

  const closeMinted = () => {
    setMinted(null);
    members.resetLink.reset();
  };

  return (
    <>
      <SectionPanel title="People" note={seatLine(rows.length, pending)}>
        {members.failure && (
          <div className="px-card py-3">
            <MutationError error={members.failure} />
          </div>
        )}
        {members.members.isError && (
          <div className="p-input">
            <ErrorState
              error={members.members.error}
              onRetry={() => void members.members.refetch()}
            />
          </div>
        )}
        {members.members.isPending && <LoadingState rows={3} className="p-input" />}
        {rows.length > 0 && (
          <Table
            height="xl"
            layoutId="settings-members"
            columns={columns({
              members,
              actor,
              owners,
              onResetLink: openResetLink,
              onRemove: setRemoving,
            })}
            rows={rows}
            rowKey={(member) => member.id}
          />
        )}
      </SectionPanel>

      <ConfirmDelete
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        entity="member"
        verb="Remove"
        // The address, not the name: two people can share a name, and this decides who loses access.
        name={removing?.userEmail ?? (removing ? nameOf(removing) : '')}
        waiting={members.remove.isPending}
        onConfirm={() => {
          if (removing) {
            members.remove.mutate(removing.userId, { onSuccess: () => setRemoving(null) });
          }
        }}
      />

      <Modal
        open={minted !== null}
        onOpenChange={closeMinted}
        width="md"
        title="A password reset link"
        description={`Shown once. Send it to ${minted ? nameOf(minted) : 'them'} somewhere private — whoever has it can set their password.`}
        footer={
          <Button size="sm" onClick={closeMinted}>
            Done
          </Button>
        }
      >
        {members.resetLink.isPending && <LoadingState rows={1} />}
        {members.resetLink.isError && (
          <Note tone="danger">
            {members.resetLink.error instanceof ApiError
              ? members.resetLink.error.message
              : 'The link wasn’t created. Try again.'}
          </Note>
        )}
        {members.resetLink.data && (
          <>
            <CopyField value={members.resetLink.data.url} label="One-time link" />
            <Hint>
              Works until {clockTime(members.resetLink.data.expiresAt, timeZone)} on{' '}
              {shortDate(members.resetLink.data.expiresAt, timeZone)}. The reset screen shows that{' '}
              {members.resetLink.data.generatedBy} sent it.
            </Hint>
          </>
        )}
      </Modal>
    </>
  );
};
